import React, {
  useMemo,
  useState
} from "react";

import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import SearchIcon from "@material-ui/icons/Search";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";

import {
  toast
} from "react-toastify";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";


const initials = value => {

  return String(
    value || "?"
  )
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(
      item =>
        item
          .charAt(0)
          .toUpperCase()
    )
    .join("");
};


const EmailAccessManager = ({
  user,
  companyId
}) => {

  const isManager =
    Boolean(
      user?.super
    );

  const [open, setOpen] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [users, setUsers] =
    useState([]);

  const [mailboxes, setMailboxes] =
    useState([]);

  const [assignments, setAssignments] =
    useState([]);

  const [
    selectedUserId,
    setSelectedUserId
  ] = useState(null);

  const [draft, setDraft] =
    useState({});


  const selectedUser =
    useMemo(
      () =>
        users.find(
          item =>
            Number(item.id) ===
            Number(selectedUserId)
        ) || null,
      [
        users,
        selectedUserId
      ]
    );


  const filteredUsers =
    useMemo(
      () => {

        const term =
          String(
            search || ""
          )
            .trim()
            .toLowerCase();

        if (!term) {
          return users;
        }

        return users.filter(
          item =>
            `${item?.name || ""} ${item?.email || ""}`
              .toLowerCase()
              .includes(term)
        );
      },
      [
        users,
        search
      ]
    );


  const loadDraft = (
    userId,
    boxRows,
    linkRows
  ) => {

    const next = {};

    boxRows.forEach(
      mailbox => {

        const link =
          linkRows.find(
            item =>
              Number(item.userId) ===
                Number(userId) &&
              Number(item.mailboxId) ===
                Number(mailbox.id)
          );

        next[
          Number(mailbox.id)
        ] = {
          canRead:
            Boolean(
              link?.canRead
            ),

          canSend:
            Boolean(
              link?.canSend
            )
        };
      }
    );

    setDraft(next);
  };


  const load = async (
    preferredUserId = null
  ) => {

    if (!companyId) {
      return;
    }

    setLoading(true);

    try {

      const {
        data
      } =
        await api.get(
          "/email-beta/access",
          {
            params: {
              companyId
            }
          }
        );

      const userRows =
        Array.isArray(
          data?.users
        )
          ? data.users
          : [];

      const boxRows =
        Array.isArray(
          data?.mailboxes
        )
          ? data.mailboxes
          : [];

      const linkRows =
        Array.isArray(
          data?.assignments
        )
          ? data.assignments
          : [];

      setUsers(
        userRows
      );

      setMailboxes(
        boxRows
      );

      setAssignments(
        linkRows
      );


      let nextUserId =
        preferredUserId;

      if (
        !userRows.some(
          item =>
            Number(item.id) ===
            Number(nextUserId)
        )
      ) {

        nextUserId =
          userRows[0]?.id ||
          null;
      }

      setSelectedUserId(
        nextUserId
      );

      if (nextUserId) {

        loadDraft(
          nextUserId,
          boxRows,
          linkRows
        );

      } else {

        setDraft({});
      }

    } catch (err) {

      toastError(err);

    } finally {

      setLoading(false);
    }
  };


  const handleOpen =
    async () => {

      setOpen(true);
      setSearch("");

      await load();
    };


  const selectUser = userId => {

    setSelectedUserId(
      userId
    );

    loadDraft(
      userId,
      mailboxes,
      assignments
    );
  };


  const toggle = (
    mailboxId,
    field
  ) => {

    setDraft(
      previous => ({
        ...previous,

        [mailboxId]: {
          ...(
            previous[
              mailboxId
            ] || {
              canRead: false,
              canSend: false
            }
          ),

          [field]:
            !previous[
              mailboxId
            ]?.[field]
        }
      })
    );
  };


  const handleSave =
    async () => {

      if (
        !companyId ||
        !selectedUserId
      ) {
        return;
      }

      const rows =
        mailboxes
          .map(
            mailbox => {

              const permission =
                draft[
                  Number(
                    mailbox.id
                  )
                ] || {
                  canRead: false,
                  canSend: false
                };

              return {
                mailboxId:
                  Number(
                    mailbox.id
                  ),

                canRead:
                  Boolean(
                    permission.canRead
                  ),

                canSend:
                  Boolean(
                    permission.canSend
                  ),

                canManage:
                  false
              };
            }
          )
          .filter(
            item =>
              item.canRead ||
              item.canSend
          );

      setSaving(true);

      try {

        await api.put(
          `/email-beta/access/${selectedUserId}`,
          {
            companyId,

            assignments:
              rows
          }
        );

        toast.success(
          "Acessos de e-mail salvos."
        );

        await load(
          selectedUserId
        );

      } catch (err) {

        toastError(err);

      } finally {

        setSaving(false);
      }
    };


  if (!isManager) {
    return null;
  }


  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        style={{
          marginBottom: 0
        }}
      >
        <Button
          variant="outlined"
          onClick={handleOpen}
          disabled={!companyId}
          startIcon={
            <SupervisorAccountOutlinedIcon />
          }
          style={{
            height: 40,
            borderRadius: 10,
            borderColor: "#D0D5DD",
            background: "#FFFFFF",
            color: "#344054",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700
          }}
        >
          Acessos de e-mail
        </Button>
      </Box>


      <Dialog
        open={open}
        onClose={
          () =>
            !saving &&
            setOpen(false)
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            maxWidth: 960,
            borderRadius: 18,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding: 0
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              padding:
                "20px 24px",
              borderBottom:
                "1px solid #EAECF0"
            }}
          >
            <Box>
              <Typography
                style={{
                  color: "#101828",
                  fontSize: 19,
                  fontWeight: 800
                }}
              >
                Acessos de e-mail
              </Typography>

              <Typography
                style={{
                  marginTop: 4,
                  color: "#667085",
                  fontSize: 11
                }}
              >
                Escolha quais caixas cada pessoa pode visualizar ou utilizar para envio.
              </Typography>
            </Box>

            <IconButton
              size="small"
              disabled={saving}
              onClick={
                () =>
                  setOpen(false)
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>


        <DialogContent
          style={{
            padding: 0,
            minHeight: 510
          }}
        >
          {loading ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 500
              }}
            >
              <CircularProgress
                size={28}
              />
            </Box>

          ) : (

            <Box
              display="flex"
              style={{
                minHeight: 510
              }}
            >
              <Box
                style={{
                  width: 285,
                  minWidth: 285,
                  background: "#FCFCFD",
                  borderRight:
                    "1px solid #EAECF0"
                }}
              >
                <Box
                  style={{
                    padding: 14
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Buscar usuário"
                    value={search}
                    onChange={
                      event =>
                        setSearch(
                          event.target.value
                        )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                        >
                          <SearchIcon
                            style={{
                              color: "#98A2B3"
                            }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                <Divider />

                <Box
                  style={{
                    maxHeight: 445,
                    overflowY: "auto",
                    padding: 9
                  }}
                >
                  {filteredUsers.map(
                    item => {

                      const active =
                        Number(
                          item.id
                        ) ===
                        Number(
                          selectedUserId
                        );

                      const count =
                        assignments.filter(
                          link =>
                            Number(
                              link.userId
                            ) ===
                              Number(
                                item.id
                              )
                        ).length;

                      return (
                        <Box
                          key={item.id}
                          onClick={
                            () =>
                              selectUser(
                                item.id
                              )
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding:
                              "10px 11px",
                            marginBottom: 4,
                            borderRadius: 10,
                            cursor: "pointer",
                            background:
                              active
                                ? "#EFF4FF"
                                : "transparent",
                            border:
                              active
                                ? "1px solid #B2CCFF"
                                : "1px solid transparent"
                          }}
                        >
                          <Avatar
                            style={{
                              width: 36,
                              height: 36,
                              fontSize: 11,
                              fontWeight: 800,
                              background:
                                active
                                  ? "#155EEF"
                                  : "#EAECF0",
                              color:
                                active
                                  ? "#FFFFFF"
                                  : "#475467"
                            }}
                          >
                            {initials(
                              item.name
                            )}
                          </Avatar>

                          <Box
                            style={{
                              minWidth: 0,
                              flex: 1
                            }}
                          >
                            <Typography
                              noWrap
                              style={{
                                color: "#101828",
                                fontSize: 12,
                                fontWeight: 700
                              }}
                            >
                              {item.name}
                            </Typography>

                            <Typography
                              noWrap
                              style={{
                                color: "#667085",
                                fontSize: 9.5,
                                marginTop: 2
                              }}
                            >
                              {item.email}
                            </Typography>
                          </Box>

                          {count > 0 && (
                            <Chip
                              label={count}
                              size="small"
                              style={{
                                height: 21,
                                fontSize: 9,
                                fontWeight: 800,
                                background: "#FFFFFF"
                              }}
                            />
                          )}
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>


              <Box
                style={{
                  flex: 1,
                  padding: 24
                }}
              >
                {!selectedUser ? (

                  <Typography
                    style={{
                      color: "#98A2B3",
                      fontSize: 12
                    }}
                  >
                    Nenhum usuário disponível.
                  </Typography>

                ) : (

                  <>
                    <Typography
                      style={{
                        color: "#101828",
                        fontSize: 17,
                        fontWeight: 800
                      }}
                    >
                      {selectedUser.name}
                    </Typography>

                    <Typography
                      style={{
                        color: "#667085",
                        fontSize: 11,
                        marginTop: 3,
                        marginBottom: 22
                      }}
                    >
                      {selectedUser.email}
                    </Typography>


                    <Typography
                      style={{
                        color: "#344054",
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        marginBottom: 10
                      }}
                    >
                      Caixas disponíveis
                    </Typography>


                    <Box
                      style={{
                        display: "grid",
                        gap: 9
                      }}
                    >
                      {mailboxes.map(
                        mailbox => {

                          const permission =
                            draft[
                              Number(
                                mailbox.id
                              )
                            ] || {
                              canRead: false,
                              canSend: false
                            };

                          const enabled =
                            permission.canRead ||
                            permission.canSend;

                          return (
                            <Paper
                              key={mailbox.id}
                              elevation={0}
                              style={{
                                padding:
                                  "13px 15px",
                                borderRadius: 11,
                                border:
                                  enabled
                                    ? "1px solid #B2CCFF"
                                    : "1px solid #EAECF0",
                                background:
                                  enabled
                                    ? "#F9FAFF"
                                    : "#FFFFFF"
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{
                                  gap: 16
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  style={{
                                    gap: 11,
                                    minWidth: 0
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 9,
                                      background:
                                        "#EEF4FF",
                                      color:
                                        "#155EEF"
                                    }}
                                  >
                                    <MailOutlineIcon
                                      style={{
                                        fontSize: 19
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    style={{
                                      minWidth: 0
                                    }}
                                  >
                                    <Typography
                                      noWrap
                                      style={{
                                        color: "#101828",
                                        fontSize: 12,
                                        fontWeight: 700
                                      }}
                                    >
                                      {mailbox.email}
                                    </Typography>

                                    {mailbox.displayName && (
                                      <Typography
                                        noWrap
                                        style={{
                                          color: "#667085",
                                          fontSize: 9.5,
                                          marginTop: 2
                                        }}
                                      >
                                        {mailbox.displayName}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>


                                <Box
                                  display="flex"
                                  alignItems="center"
                                  style={{
                                    gap: 11
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Checkbox
                                      checked={
                                        Boolean(
                                          permission.canRead
                                        )
                                      }
                                      color="primary"
                                      size="small"
                                      onChange={
                                        () =>
                                          toggle(
                                            Number(
                                              mailbox.id
                                            ),
                                            "canRead"
                                          )
                                      }
                                    />

                                    <Typography
                                      style={{
                                        color: "#475467",
                                        fontSize: 10.5,
                                        fontWeight: 600
                                      }}
                                    >
                                      Ler
                                    </Typography>
                                  </Box>

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Checkbox
                                      checked={
                                        Boolean(
                                          permission.canSend
                                        )
                                      }
                                      color="primary"
                                      size="small"
                                      onChange={
                                        () =>
                                          toggle(
                                            Number(
                                              mailbox.id
                                            ),
                                            "canSend"
                                          )
                                      }
                                    />

                                    <Typography
                                      style={{
                                        color: "#475467",
                                        fontSize: 10.5,
                                        fontWeight: 600
                                      }}
                                    >
                                      Enviar
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          );
                        }
                      )}

                      {!mailboxes.length && (
                        <Typography
                          style={{
                            color: "#98A2B3",
                            fontSize: 12
                          }}
                        >
                          Nenhuma caixa disponível nesta empresa.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>


        <DialogActions
          style={{
            padding: "15px 24px",
            borderTop:
              "1px solid #EAECF0"
          }}
        >
          <Button
            onClick={
              () =>
                setOpen(false)
            }
            disabled={saving}
            style={{
              textTransform: "none",
              fontWeight: 700
            }}
          >
            Fechar
          </Button>

          <Button
            color="primary"
            variant="contained"
            disabled={
              saving ||
              loading ||
              !selectedUserId
            }
            onClick={handleSave}
            style={{
              minWidth: 145,
              height: 39,
              borderRadius: 9,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 800
            }}
          >
            {saving ? (
              <CircularProgress
                size={18}
                color="inherit"
              />
            ) : (
              "Salvar acessos"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


export default EmailAccessManager;
